import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  List,
  ListItem,
  Text,
  useOutsideClick,
  Spinner,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';

export default function LocationAutocomplete({
  placeholder = 'Buscar localidad...',
  value = '',
  onChange,
  onSelect,
  name,
  leftIcon,
  rightElement,
  ...props
}) {
  const [inputValue, setInputValue] = useState(value);
  const [predictions, setPredictions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false),
  });

  // Initialize Google Places services
  useEffect(() => {
    if (window.google) {
      setAutocompleteService(
        new window.google.maps.places.AutocompleteService()
      );
      // Create a dummy div for PlacesService (it requires a map or div)
      const div = document.createElement('div');
      setPlacesService(new window.google.maps.places.PlacesService(div));
    }
  }, []);

  // Update input value when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Fetch predictions from Google Places
  const fetchPredictions = input => {
    if (!input || input.length < 2 || !autocompleteService) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);

    autocompleteService.getPlacePredictions(
      {
        input,
        types: ['geocode'],
      },
      (results, status) => {
        setIsLoading(false);
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          results
        ) {
          setPredictions(results);
        } else {
          setPredictions([]);
        }
      }
    );
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPredictions(inputValue);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue, autocompleteService]);

  const handleInputChange = e => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (onChange) {
      onChange({
        target: {
          name,
          value: newValue,
        },
      });
    }
  };

  const handleSelect = prediction => {
    const selectedValue = prediction.description;
    setInputValue(selectedValue);
    setIsOpen(false);
    setPredictions([]);

    // Get place details
    if (placesService) {
      placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['formatted_address', 'geometry', 'name', 'place_id'],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            if (onSelect) {
              onSelect({
                displayText: selectedValue,
                name: place.name,
                formattedAddress: place.formatted_address,
                placeId: place.place_id,
                location: place.geometry?.location,
              });
            }
          }
        }
      );
    }

    if (onChange) {
      onChange({
        target: {
          name,
          value: selectedValue,
        },
      });
    }
  };

  const handleClear = () => {
    setInputValue('');
    setPredictions([]);
    setIsOpen(false);

    if (onChange) {
      onChange({
        target: {
          name,
          value: '',
        },
      });
    }

    inputRef.current?.focus();
  };

  const handleKeyDown = e => {
    if (!isOpen || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < predictions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
          handleSelect(predictions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('li');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
        });
      }
    }
  }, [highlightedIndex]);

  return (
    <Box position="relative" ref={containerRef} w="100%">
      <InputGroup>
        {leftIcon && <InputLeftElement>{leftIcon}</InputLeftElement>}
        <Input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 2 && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          name={name}
          autoComplete="off"
          {...props}
        />
        <InputRightElement width="auto" pr={1}>
          {isLoading && <Spinner size="sm" mr={2} />}
          {inputValue && (
            <IconButton
              size="xs"
              variant="ghost"
              icon={<CloseIcon />}
              onClick={handleClear}
              aria-label="Limpiar"
            />
          )}
          {rightElement}
        </InputRightElement>
      </InputGroup>

      {isOpen && predictions.length > 0 && (
        <List
          ref={listRef}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          bg="white"
          boxShadow="lg"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          maxH="300px"
          overflowY="auto"
          zIndex={1000}
          mt={1}
        >
          {predictions.map((prediction, index) => (
            <ListItem
              key={prediction.place_id}
              px={4}
              py={3}
              cursor="pointer"
              bg={highlightedIndex === index ? 'blue.50' : 'white'}
              _hover={{ bg: 'gray.100' }}
              onClick={() => handleSelect(prediction)}
              borderBottom={
                index < predictions.length - 1 ? '1px solid' : 'none'
              }
              borderColor="gray.100"
            >
              <Text fontSize="sm" fontWeight="medium">
                {prediction.structured_formatting?.main_text ||
                  prediction.description}
              </Text>
              {prediction.structured_formatting?.secondary_text && (
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                  {prediction.structured_formatting.secondary_text}
                </Text>
              )}
            </ListItem>
          ))}
        </List>
      )}

      {isOpen &&
        inputValue.length >= 2 &&
        predictions.length === 0 &&
        !isLoading && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            bg="white"
            boxShadow="lg"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            p={4}
            zIndex={1000}
            mt={1}
          >
            <Text fontSize="sm" color="gray.500" textAlign="center">
              No se encontraron resultados
            </Text>
          </Box>
        )}
    </Box>
  );
}
